import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Plus, Receipt, Filter, Calendar, Edit2, Trash2, RefreshCw, 
  CheckCircle, Clock, AlertCircle, DollarSign, Users, Search, Eye, IndianRupee,
  Copy, Building2, UserPlus, FileSpreadsheet, X
} from 'lucide-react';
import * as XLSX from 'xlsx';

const EXPENSE_CATEGORIES = [
  'Transportation',
  'Packaging',
  'Labor',
  'Fuel',
  'Loading/Unloading',
  'Commission',
  'Cold Storage',
  'Market Fees',
  'Maintenance',
  'Other'
];

const PAYMENT_MODES = ['Cash', 'UPI', 'Bank Transfer', 'Cheque'];

const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-orange-100 text-orange-700' },
  { value: 'partially_paid', label: 'Partially Paid', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'paid', label: 'Paid', color: 'bg-green-100 text-green-700' }
];

const VERTICAL_OPTIONS = [
  { value: 'all', label: 'All (Split Equally)' },
  { value: 'qc', label: 'QC Only' },
  { value: 'retail', label: 'Retail Only' }
];

export default function VariableExpenses() {
  const [expenses, setExpenses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]); // Admin and Staff users
  const [retailers, setRetailers] = useState([]); // Retailers for retail vertical
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showSettlementDialog, setShowSettlementDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  
  // Filters
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSettled, setFilterSettled] = useState('all');
  const [filterPaidBy, setFilterPaidBy] = useState('all'); // New filter for Paid By
  const [filterPaidTo, setFilterPaidTo] = useState('all'); // Filter for Paid To (vendor)
  
  // Form data
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    description: '',
    rate: '',
    quantity: '',
    amount: '',
    paid_to: '',
    vendor_id: '', // Add vendor_id for linking
    payment_mode: 'Cash',
    receipt_no: '',
    remarks: '',
    paid_by_type: 'company', // 'company' or 'employee'
    paid_by: 'Company', // Company or Employee name
    paid_by_employee_id: '', // If employee, store their ID
    payment_status: 'pending', // 'pending', 'partially_paid', 'paid'
    paid_amount: '',
    payment_date: '', // Empty by default - will be filled when user enters payment details
    payment_reference: '',
    settlement_status: 'settled', // 'settled' or 'pending_reimbursement'
    settlement_date: '',
    settlement_remarks: '',
    vertical: 'all', // 'all', 'qc', or 'retail'
    split_type: 'all_equal', // 'all_equal' | 'selected' | 'proportional'
    retailer_ids: [] // For 'selected' split type
  });
  
  // Payment Details Modal state (for recording payment details when marking as Paid)
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [paymentDetailsForm, setPaymentDetailsForm] = useState({
    payment_mode: 'Cash',
    payment_date: new Date().toISOString().split('T')[0],
    payment_reference: '',
    paid_by_type: 'company',
    paid_by_employee_id: ''
  });
  
  // Bulk settlement
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [settlementRemarks, setSettlementRemarks] = useState('');

  // Search box inside the Add/Edit expense retailer picker ('selected' split type)
  const [retailerPickerSearch, setRetailerPickerSearch] = useState('');
  
  // View Payment Modal state
  const [showViewPaymentModal, setShowViewPaymentModal] = useState(false);
  const [viewingExpense, setViewingExpense] = useState(null);
  
  // Reimbursement Modal state (for settling employee expenses)
  const [showReimbursementModal, setShowReimbursementModal] = useState(false);
  const [reimbursementExpense, setReimbursementExpense] = useState(null);
  const [reimbursementForm, setReimbursementForm] = useState({
    amount: 0,  // Amount being reimbursed
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'Bank Transfer',
    payment_reference: '',
    remarks: '',
    custom_amount: ''  // For editable reimbursement amount
  });
  
  // Bulk Settlement state
  const [showBulkSettlementModal, setShowBulkSettlementModal] = useState(false);
  const [bulkSettlementEmployee, setBulkSettlementEmployee] = useState(null);
  const [bulkSettlementExpenses, setBulkSettlementExpenses] = useState([]);
  
  // Vendor Bulk Payment state (new - like Procurement)
  const [showVendorPaymentModal, setShowVendorPaymentModal] = useState(false);
  const [vendorPaymentForm, setVendorPaymentForm] = useState({
    payment_mode: 'Cash',
    reference: '',
    remarks: '',
    custom_amount: ''
  });
  const [selectedVendorForPayment, setSelectedVendorForPayment] = useState(null);
  const [vendorsWithPending, setVendorsWithPending] = useState([]);

  // Vendor Management state
  const [vendors, setVendors] = useState([]);
  const [showVendorDialog, setShowVendorDialog] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [vendorForm, setVendorForm] = useState({ name: '', contact: '', phone: '', notes: '' });
  const [showVendorManagement, setShowVendorManagement] = useState(false);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/api/expenses/variable';
      const params = new URLSearchParams();
      if (filterDateFrom) params.append('from_date', filterDateFrom);
      if (filterDateTo) params.append('to_date', filterDateTo);
      if (filterCategory !== 'all') params.append('category', filterCategory);
      if (filterSettled !== 'all') params.append('settled', filterSettled);
      if (filterPaidBy !== 'all') params.append('paid_by', filterPaidBy);
      if (filterPaidTo !== 'all') params.append('paid_to', filterPaidTo);
      
      if (params.toString()) url += '?' + params.toString();
      
      const response = await api.get(url);
      setExpenses(response.data);
    } catch (error) {
      console.error('Load expenses error:', error);
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [filterDateFrom, filterDateTo, filterCategory, filterSettled, filterPaidBy, filterPaidTo]);

  const loadEmployees = async () => {
    try {
      const response = await api.get('/api/employees');
      setEmployees(response.data);
    } catch (error) {
      // Employees list might not exist yet, use a default list
      setEmployees([]);
    }
  };

  // Load Admin and Staff users for Paid By dropdown
  const loadStaffUsers = async () => {
    try {
      const response = await api.get('/api/employees');
      setStaffUsers(response.data || []);
    } catch (error) {
      console.error('Failed to load staff users:', error);
      setStaffUsers([]);
    }
  };

  // Load Retailers for retail vertical dropdown
  const loadRetailers = async () => {
    try {
      const response = await api.get('/api/retailers');
      setRetailers(response.data || []);
    } catch (error) {
      console.error('Failed to load retailers:', error);
      setRetailers([]);
    }
  };

  // Load Vendors for dropdown
  const loadVendors = async () => {
    try {
      const response = await api.get('/api/vendors');
      setVendors(response.data || []);
    } catch (error) {
      console.error('Failed to load vendors:', error);
      setVendors([]);
    }
  };

  useEffect(() => {
    loadExpenses();
    loadEmployees();
    loadStaffUsers();
    loadRetailers();
    loadVendors();
  }, [loadExpenses]);

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      category: '',
      description: '',
      rate: '',
      quantity: '',
      amount: '',
      paid_to: '',
      vendor_id: '',
      payment_mode: 'Cash',
      receipt_no: '',
      remarks: '',
      paid_by_type: 'company',
      paid_by: 'Company',
      paid_by_employee_id: '',
      payment_status: 'pending',
      paid_amount: '',
      payment_date: '', // Empty by default - will be filled when user enters payment details
      payment_reference: '',
      settlement_status: 'settled',
      settlement_date: '',
      settlement_remarks: '',
      vertical: 'all',
      split_type: 'all_equal',
      retailer_ids: []
    });
    setEditingExpense(null);
    setRetailerPickerSearch('');
  };

  const handleSubmit = async () => {
    if (!formData.category || !formData.amount) {
      toast.error('Category and Amount are required');
      return;
    }
    
    // Validate Vendor is selected
    if (!formData.vendor_id) {
      toast.error('Please select a vendor from the dropdown');
      return;
    }
    
    // Validate transaction reference for non-cash payments
    if (formData.payment_mode !== 'Cash' && !formData.payment_reference?.trim()) {
      toast.error('Transaction Reference is required for non-cash payments');
      return;
    }
    
    // If status is being set to "paid", show payment details modal first
    // IMPORTANT: Close the Add Dialog FIRST to avoid focus-trap issues
    if (formData.payment_status === 'paid' && !formData.payment_date) {
      setPaymentDetailsForm({
        payment_mode: formData.payment_mode || 'Cash',
        payment_date: new Date().toISOString().split('T')[0],
        payment_reference: formData.payment_reference || '',
        paid_by_type: formData.paid_by_type || 'company',
        paid_by_employee_id: formData.paid_by_employee_id || ''
      });
      setShowAddDialog(false); // Close Add Dialog to release focus trap
      setShowPaymentDetailsModal(true);
      return;
    }
    
    try {
      // Determine paid_by value based on type
      let paidByValue = 'Company';
      if (formData.paid_by_type === 'employee' && formData.paid_by_employee_id) {
        const emp = staffUsers.find(u => u.id === formData.paid_by_employee_id);
        paidByValue = emp?.name || emp?.email || formData.paid_by_employee_id;
      }
      
      // Settlement status: Company paid = settled, Employee paid = pending_reimbursement (unless already settled)
      let settlementStatus = formData.settlement_status;
      if (formData.payment_status === 'paid') {
        if (formData.paid_by_type === 'company') {
          settlementStatus = 'settled';
        } else if (formData.paid_by_type === 'employee' && formData.settlement_status !== 'settled') {
          settlementStatus = 'pending_reimbursement';
        }
      }
      
      const payload = {
        ...formData,
        rate: formData.rate ? parseFloat(formData.rate) : null,
        quantity: formData.quantity ? parseFloat(formData.quantity) : null,
        amount: parseFloat(formData.amount),
        paid_amount: formData.payment_status === 'paid' ? parseFloat(formData.amount) : (formData.paid_amount ? parseFloat(formData.paid_amount) : 0),
        payment_status: formData.payment_status || 'pending',
        is_settled: formData.payment_status === 'paid' && formData.paid_by_type === 'company',
        paid_by_type: formData.paid_by_type,
        paid_by: paidByValue,
        paid_by_employee_id: formData.paid_by_employee_id,
        payment_date: formData.payment_date,
        payment_reference: formData.payment_reference,
        settlement_status: settlementStatus,
        vendor_id: formData.vendor_id || null,
        // New split fields for retail vertical
        split_type: formData.vertical === 'retail' ? formData.split_type : null,
        retailer_ids: formData.vertical === 'retail' && formData.split_type === 'selected' ? formData.retailer_ids : []
      };
      
      if (editingExpense) {
        await api.put(`/api/expenses/variable/${editingExpense.id}`, payload);
        toast.success('Expense updated successfully');
      } else {
        await api.post('/api/expenses/variable', payload);
        toast.success('Expense added successfully');
      }
      
      setShowAddDialog(false);
      resetForm();
      loadExpenses();
    } catch (error) {
      console.error('Save expense error:', error);
      toast.error('Failed to save expense');
    }
  };
  
  // Handle payment details submission from modal
  const handlePaymentDetailsSubmit = () => {
    // Determine paid_by value
    let paidByValue = 'Company';
    if (paymentDetailsForm.paid_by_type === 'employee' && paymentDetailsForm.paid_by_employee_id) {
      const emp = staffUsers.find(u => u.id === paymentDetailsForm.paid_by_employee_id);
      paidByValue = emp?.name || emp?.email || paymentDetailsForm.paid_by_employee_id;
    }
    
    // Update form with payment details and submit
    setFormData(prev => ({
      ...prev,
      payment_mode: paymentDetailsForm.payment_mode,
      payment_date: paymentDetailsForm.payment_date,
      payment_reference: paymentDetailsForm.payment_reference,
      paid_by_type: paymentDetailsForm.paid_by_type,
      paid_by_employee_id: paymentDetailsForm.paid_by_employee_id,
      paid_by: paidByValue
    }));
    setShowPaymentDetailsModal(false);
    // Re-trigger submit with complete data
    setTimeout(() => handleSubmitFinal(), 100);
  };
  
  // Final submit after payment details
  const handleSubmitFinal = async () => {
    try {
      let paidByValue = 'Company';
      if (formData.paid_by_type === 'employee' && formData.paid_by_employee_id) {
        const emp = staffUsers.find(u => u.id === formData.paid_by_employee_id);
        paidByValue = emp?.name || emp?.email || formData.paid_by_employee_id;
      }
      
      // Settlement status: Company paid = settled, Employee paid = pending_reimbursement
      let settlementStatus = 'settled';
      let isSettled = true;
      if (formData.payment_status === 'paid' && formData.paid_by_type === 'employee') {
        settlementStatus = 'pending_reimbursement';
        isSettled = false;  // Not settled until company reimburses employee
      }
      
      const payload = {
        ...formData,
        rate: formData.rate ? parseFloat(formData.rate) : null,
        quantity: formData.quantity ? parseFloat(formData.quantity) : null,
        amount: parseFloat(formData.amount),
        paid_amount: formData.payment_status === 'paid' ? parseFloat(formData.amount) : (formData.paid_amount ? parseFloat(formData.paid_amount) : 0),
        payment_status: formData.payment_status || 'pending',
        is_settled: isSettled,
        paid_by_type: formData.paid_by_type,
        paid_by: paidByValue,
        paid_by_employee_id: formData.paid_by_employee_id,
        payment_date: formData.payment_date,
        payment_reference: formData.payment_reference,
        settlement_status: settlementStatus
      };
      
      if (editingExpense) {
        await api.put(`/api/expenses/variable/${editingExpense.id}`, payload);
        toast.success('Expense updated successfully');
      } else {
        await api.post('/api/expenses/variable', payload);
        toast.success('Expense added successfully');
      }
      
      setShowAddDialog(false);
      resetForm();
      loadExpenses();
    } catch (error) {
      console.error('Save expense error:', error);
      toast.error('Failed to save expense');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    // Determine paid_by_type from existing data
    const isPaidByEmployee = expense.paid_by && expense.paid_by !== 'Company' && expense.paid_by !== 'Vendor';
    setFormData({
      date: expense.date?.split('T')[0] || '',
      category: expense.category || '',
      description: expense.description || '',
      rate: expense.rate?.toString() || '',
      quantity: expense.quantity?.toString() || '',
      amount: expense.amount?.toString() || '',
      paid_to: expense.paid_to || '',
      vendor_id: expense.vendor_id || '',
      payment_mode: expense.payment_mode || 'Cash',
      receipt_no: expense.receipt_no || '',
      remarks: expense.remarks || '',
      paid_by_type: expense.paid_by_type || (isPaidByEmployee ? 'employee' : 'company'),
      paid_by: expense.paid_by || 'Company',
      paid_by_employee_id: expense.paid_by_employee_id || '',
      payment_status: expense.payment_status || (expense.is_settled ? 'paid' : 'pending'),
      paid_amount: expense.paid_amount?.toString() || '',
      payment_date: expense.payment_date?.split('T')[0] || '',
      payment_reference: expense.payment_reference || '',
      settlement_status: expense.settlement_status || (isPaidByEmployee && !expense.is_settled ? 'pending_reimbursement' : 'settled'),
      settlement_date: expense.settlement_date?.split('T')[0] || '',
      settlement_remarks: expense.settlement_remarks || '',
      vertical: expense.vertical || 'all',
      split_type: expense.split_type || 'all_equal',
      retailer_ids: expense.retailer_ids || [],
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      await api.delete(`/api/expenses/variable/${id}`);
      toast.success('Expense deleted');
      loadExpenses();
    } catch (error) {
      console.error('Delete expense error:', error);
      toast.error('Failed to delete expense');
    }
  };

  const handleBulkSettle = async () => {
    if (selectedExpenses.length === 0) {
      toast.error('Select expenses to settle');
      return;
    }
    
    try {
      await api.post('/api/expenses/variable/bulk-settle', {
        expense_ids: selectedExpenses,
        settlement_date: new Date().toISOString().split('T')[0],
        settlement_remarks: settlementRemarks
      });
      toast.success(`${selectedExpenses.length} expenses settled`);
      setShowSettlementDialog(false);
      setSelectedExpenses([]);
      setSettlementRemarks('');
      loadExpenses();
    } catch (error) {
      console.error('Bulk settle error:', error);
      toast.error('Failed to settle expenses');
    }
  };

  const toggleExpenseSelection = (id) => {
    setSelectedExpenses(prev => 
      prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]
    );
  };

  // View payment details
  const handleViewPayment = (expense) => {
    setViewingExpense(expense);
    setShowViewPaymentModal(true);
  };

  // Open reimbursement modal for a single expense
  const openReimbursementModal = (expense) => {
    // Calculate remaining amount to reimburse
    const totalReimbursed = expense.total_reimbursed || 0;
    const remainingAmount = expense.amount - totalReimbursed;
    
    setReimbursementExpense(expense);
    setReimbursementForm({
      amount: remainingAmount,  // Pre-fill with remaining amount
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'Bank Transfer',
      payment_reference: '',
      remarks: ''
    });
    setShowReimbursementModal(true);
  };

  // Handle single expense reimbursement
  const handleReimbursementSubmit = async () => {
    if (!reimbursementExpense) return;
    
    const reimbursementAmount = parseFloat(reimbursementForm.amount) || 0;
    if (reimbursementAmount <= 0) {
      toast.error('Please enter a valid reimbursement amount');
      return;
    }
    
    const totalReimbursed = (reimbursementExpense.total_reimbursed || 0) + reimbursementAmount;
    const expenseTotal = reimbursementExpense.amount || 0;
    
    // Determine settlement status based on total reimbursed vs expense amount
    let settlementStatus = 'partial';
    if (totalReimbursed >= expenseTotal) {
      settlementStatus = 'settled';
    }
    
    try {
      await api.put(`/api/expenses/variable/${reimbursementExpense.id}`, {
        settlement_status: settlementStatus,
        settlement_date: reimbursementForm.payment_date,
        settlement_mode: reimbursementForm.payment_mode,
        settlement_reference: reimbursementForm.payment_reference,
        settlement_remarks: reimbursementForm.remarks,
        total_reimbursed: totalReimbursed,
        last_reimbursement_amount: reimbursementAmount,
        is_settled: settlementStatus === 'settled'
      });
      toast.success('Reimbursement recorded successfully');
      setShowReimbursementModal(false);
      setReimbursementExpense(null);
      loadExpenses();
    } catch (error) {
      console.error('Reimbursement error:', error);
      toast.error('Failed to record reimbursement');
    }
  };

  // Settle employee reimbursement (quick action)
  const handleSettleReimbursement = async (expense) => {
    try {
      // Only send the fields that need to be updated
      await api.put(`/api/expenses/variable/${expense.id}`, {
        settlement_status: 'settled',
        settlement_date: new Date().toISOString().split('T')[0],
        is_settled: true
      });
      toast.success('Reimbursement settled');
      setShowViewPaymentModal(false);
      loadExpenses();
    } catch (error) {
      console.error('Settlement error:', error);
      toast.error('Failed to settle reimbursement');
    }
  };

  // Get employees with pending reimbursements
  // Note: We only check paid_by_type and settlement_status, NOT payment_status
  // Because when an employee pays for an expense, the vendor is effectively paid by the employee
  // We need to reimburse the employee regardless of the payment_status field
  const getEmployeesWithPendingReimbursements = () => {
    const pending = expenses.filter(e => 
      e.paid_by_type === 'employee' && 
      e.settlement_status !== 'settled'
    );
    
    const byEmployee = {};
    pending.forEach(e => {
      const empName = e.paid_by || 'Unknown';
      if (!byEmployee[empName]) {
        byEmployee[empName] = { name: empName, expenses: [], total: 0, totalReimbursed: 0 };
      }
      byEmployee[empName].expenses.push(e);
      byEmployee[empName].total += e.amount || 0;
      byEmployee[empName].totalReimbursed += e.total_reimbursed || 0;
    });
    
    return Object.values(byEmployee);
  };

  // Open bulk settlement modal for an employee
  const openBulkSettlementModal = (employee) => {
    // Calculate remaining amount for each expense (total - already reimbursed)
    const employeeWithRemaining = {
      ...employee,
      expenses: employee.expenses.map(e => ({
        ...e,
        remaining_amount: (e.amount || 0) - (e.total_reimbursed || 0)
      })).filter(e => e.remaining_amount > 0),  // Only show expenses with remaining amount
      total_remaining: employee.expenses.reduce((sum, e) => sum + ((e.amount || 0) - (e.total_reimbursed || 0)), 0)
    };
    
    setBulkSettlementEmployee(employeeWithRemaining);
    setBulkSettlementExpenses([]);  // No longer using checkboxes
    setReimbursementForm({
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'Bank Transfer',
      payment_reference: '',
      remarks: '',
      custom_amount: ''
    });
    setShowBulkSettlementModal(true);
  };

  // Handle bulk settlement (employee reimbursement with auto-allocation)
  const handleBulkSettlement = async () => {
    if (!bulkSettlementEmployee) return;
    
    // Validate transaction reference for non-cash payments
    if (reimbursementForm.payment_mode !== 'Cash' && !reimbursementForm.payment_reference?.trim()) {
      toast.error('Transaction Reference is required for non-cash payments');
      return;
    }
    
    const reimbursementAmount = reimbursementForm.custom_amount 
      ? parseFloat(reimbursementForm.custom_amount) 
      : bulkSettlementEmployee.total_remaining;
    
    if (reimbursementAmount <= 0) {
      toast.error('Reimbursement amount must be greater than 0');
      return;
    }
    
    try {
      // Sort expenses by date (oldest first)
      const sortedExpenses = [...bulkSettlementEmployee.expenses].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      
      let remainingReimbursement = reimbursementAmount;
      const settlementReference = reimbursementForm.payment_reference || `REIMB-${Date.now()}`;
      let settledCount = 0;
      let partialCount = 0;
      
      for (const expense of sortedExpenses) {
        if (remainingReimbursement <= 0) break;
        
        const expenseRemaining = expense.remaining_amount || ((expense.amount || 0) - (expense.total_reimbursed || 0));
        const reimburseAmount = Math.min(remainingReimbursement, expenseRemaining);
        
        if (reimburseAmount > 0) {
          const newTotalReimbursed = (expense.total_reimbursed || 0) + reimburseAmount;
          const isFullySettled = Math.abs(newTotalReimbursed - expense.amount) < 0.01;
          
          await api.put(`/api/expenses/variable/${expense.id}`, {
            total_reimbursed: newTotalReimbursed,
            settlement_status: isFullySettled ? 'settled' : 'partial_reimbursement',
            settlement_date: reimbursementForm.payment_date,
            settlement_mode: reimbursementForm.payment_mode,
            settlement_reference: settlementReference,
            settlement_remarks: reimbursementForm.remarks,
            is_settled: isFullySettled
          });
          
          if (isFullySettled) settledCount++;
          else partialCount++;
          
          remainingReimbursement -= reimburseAmount;
        }
      }
      
      let successMsg = `Reimbursed ₹${reimbursementAmount.toFixed(2)} to ${bulkSettlementEmployee.name}. `;
      if (settledCount > 0) successMsg += `${settledCount} fully settled. `;
      if (partialCount > 0) successMsg += `${partialCount} partially settled.`;
      
      toast.success(successMsg);
      setShowBulkSettlementModal(false);
      setBulkSettlementEmployee(null);
      setBulkSettlementExpenses([]);
      loadExpenses();
    } catch (error) {
      console.error('Bulk settlement error:', error);
      toast.error('Failed to settle expenses');
    }
  };

  // Calculate vendors/employees with pending payments
  const getVendorsWithPendingPayments = useCallback(() => {
    const vendorMap = {};
    expenses.forEach(expense => {
      if (expense.payment_status !== 'paid') {
        const vendorKey = expense.paid_to || 'Unknown';
        if (!vendorMap[vendorKey]) {
          vendorMap[vendorKey] = {
            name: vendorKey,
            expenses: [],
            total_pending: 0
          };
        }
        const pending = (expense.amount || 0) - (expense.paid_amount || 0);
        if (pending > 0) {
          vendorMap[vendorKey].expenses.push(expense);
          vendorMap[vendorKey].total_pending += pending;
        }
      }
    });
    return Object.values(vendorMap).filter(v => v.total_pending > 0).sort((a, b) => b.total_pending - a.total_pending);
  }, [expenses]);

  // Open vendor payment modal
  const openVendorPaymentModal = () => {
    const vendors = getVendorsWithPendingPayments();
    setVendorsWithPending(vendors);
    setSelectedVendorForPayment(null);
    setVendorPaymentForm({
      payment_mode: 'Cash',
      reference: '',
      remarks: '',
      custom_amount: ''
    });
    setShowVendorPaymentModal(true);
  };

  // Handle vendor bulk payment
  const handleVendorBulkPayment = async () => {
    if (!selectedVendorForPayment) {
      toast.error('Please select a vendor');
      return;
    }

    // Validate transaction reference for non-cash payments
    if (vendorPaymentForm.payment_mode !== 'Cash' && !vendorPaymentForm.reference?.trim()) {
      toast.error('Transaction Reference is required for non-cash payments');
      return;
    }

    const paymentAmount = vendorPaymentForm.custom_amount 
      ? parseFloat(vendorPaymentForm.custom_amount) 
      : selectedVendorForPayment.total_pending;

    if (paymentAmount <= 0) {
      toast.error('Payment amount must be greater than 0');
      return;
    }

    try {
      // Sort expenses by date (oldest first)
      const sortedExpenses = [...selectedVendorForPayment.expenses].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );

      let remainingPayment = paymentAmount;
      const paymentReference = vendorPaymentForm.reference || `BULK-VE-${Date.now()}`;
      let updatedCount = 0;
      let partialCount = 0;

      for (const expense of sortedExpenses) {
        if (remainingPayment <= 0) break;

        const currentPending = (expense.amount || 0) - (expense.paid_amount || 0);
        const payAmount = Math.min(remainingPayment, currentPending);

        if (payAmount > 0) {
          const newPaidAmount = (expense.paid_amount || 0) + payAmount;
          const isFullyPaid = Math.abs(newPaidAmount - expense.amount) < 0.01;

          await api.put(`/api/expenses/variable/${expense.id}`, {
            paid_amount: newPaidAmount,
            payment_status: isFullyPaid ? 'paid' : 'partially_paid',
            payment_date: new Date().toISOString().split('T')[0],
            payment_mode: vendorPaymentForm.payment_mode,
            payment_reference: paymentReference,
            is_settled: isFullyPaid,
            settlement_status: isFullyPaid ? 'settled' : expense.settlement_status
          });

          if (isFullyPaid) updatedCount++;
          else partialCount++;
          
          remainingPayment -= payAmount;
        }
      }

      let successMsg = `Payment of ₹${paymentAmount.toFixed(2)} recorded for ${selectedVendorForPayment.name}. `;
      if (updatedCount > 0) successMsg += `${updatedCount} fully paid. `;
      if (partialCount > 0) successMsg += `${partialCount} partially paid.`;

      toast.success(successMsg);
      setShowVendorPaymentModal(false);
      setSelectedVendorForPayment(null);
      setVendorPaymentForm({ payment_mode: 'Cash', reference: '', remarks: '', custom_amount: '' });
      loadExpenses();
    } catch (error) {
      console.error('Vendor bulk payment error:', error);
      toast.error('Failed to record payment');
    }
  };

  // ==================== VENDOR MANAGEMENT ====================
  
  const resetVendorForm = () => {
    setVendorForm({ name: '', contact: '', phone: '', notes: '' });
    setEditingVendor(null);
  };

  const handleVendorSubmit = async () => {
    if (!vendorForm.name?.trim()) {
      toast.error('Vendor name is required');
      return;
    }

    try {
      if (editingVendor) {
        await api.put(`/api/vendors/${editingVendor.id}`, vendorForm);
        toast.success('Vendor updated successfully');
      } else {
        await api.post('/api/vendors', vendorForm);
        toast.success('Vendor added successfully');
      }
      setShowVendorDialog(false);
      resetVendorForm();
      loadVendors();
    } catch (error) {
      console.error('Save vendor error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    }
  };

  const handleEditVendor = (vendor) => {
    setEditingVendor(vendor);
    setVendorForm({
      name: vendor.name || '',
      contact: vendor.contact || '',
      phone: vendor.phone || '',
      notes: vendor.notes || ''
    });
    setShowVendorDialog(true);
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;

    try {
      await api.delete(`/api/vendors/${vendorId}`);
      toast.success('Vendor deleted');
      loadVendors();
    } catch (error) {
      console.error('Delete vendor error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete vendor');
    }
  };

  const migrateVendorsFromExpenses = async () => {
    try {
      const response = await api.post('/api/vendors/migrate-from-expenses');
      toast.success(response.data.message);
      loadVendors();
    } catch (error) {
      console.error('Migration error:', error);
      toast.error('Failed to migrate vendors');
    }
  };

  // ==================== COPY EXPENSE ====================
  
  const handleCopyExpense = (expense) => {
    // Create a copy of the expense with today's date
    setFormData({
      date: new Date().toISOString().split('T')[0],
      category: expense.category || '',
      description: expense.description || '',
      rate: expense.rate?.toString() || '',
      quantity: expense.quantity?.toString() || '',
      amount: expense.amount?.toString() || '',
      paid_to: expense.paid_to || '',
      vendor_id: expense.vendor_id || '',
      payment_mode: expense.payment_mode || 'Cash',
      receipt_no: '', // Clear receipt for new entry
      remarks: expense.remarks || '',
      paid_by_type: expense.paid_by_type || 'company',
      paid_by: expense.paid_by || 'Company',
      paid_by_employee_id: expense.paid_by_employee_id || '',
      payment_status: 'pending', // Reset to pending for new entry
      paid_amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_reference: '',
      settlement_status: 'settled',
      settlement_date: '',
      settlement_remarks: '',
      vertical: expense.vertical || 'all',
      split_type: expense.split_type || 'all_equal',
      retailer_ids: expense.retailer_ids || [],
    });
    setEditingExpense(null); // Not editing - creating a copy
    setShowAddDialog(true);
    toast.info('Expense copied - adjust values and save as new');
  };

  const employeesWithPendingReimbursements = getEmployeesWithPendingReimbursements();

  const unsettledExpenses = expenses.filter(e => e.payment_status !== 'paid' && !e.is_settled);
  const pendingExpenses = expenses.filter(e => e.payment_status === 'pending');
  const partiallyPaidExpenses = expenses.filter(e => e.payment_status === 'partially_paid');
  // Pending reimbursement = any expense paid by an employee that hasn't been settled yet
  // We don't check payment_status because when employee pays, the vendor is effectively paid
  const pendingReimbursementExpenses = expenses.filter(e => e.paid_by_type === 'employee' && e.settlement_status !== 'settled');
  const totalAmount = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const unsettledAmount = unsettledExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  // Calculate remaining reimbursement amount (total - already reimbursed)
  const pendingReimbursementAmount = pendingReimbursementExpenses.reduce((sum, e) => sum + ((e.amount || 0) - (e.total_reimbursed || 0)), 0);
  const paidAmount = expenses.filter(e => e.payment_status === 'paid' || e.is_settled).reduce((sum, e) => sum + (e.amount || 0), 0);
  
  // Group by category for summary
  const categoryTotals = expenses.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + (e.amount || 0);
    return acc;
  }, {});

  // Export to Excel function
  const exportToExcel = () => {
    if (expenses.length === 0) {
      toast.error('No expenses to export');
      return;
    }

    try {
      // Prepare data for export
      const exportData = expenses.map((expense, index) => ({
        'S.No': index + 1,
        'Date': expense.date ? new Date(expense.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
        'Category': expense.category || '',
        'Description': expense.description || '',
        'Rate (₹)': expense.rate || 0,
        'Quantity': expense.quantity || 1,
        'Amount (₹)': expense.amount || 0,
        'Vertical': expense.vertical === 'all' ? 'All' : expense.vertical === 'qc' ? 'QC' : expense.vertical === 'retail' ? 'Retail' : expense.vertical || 'All',
        'Paid To': expense.paid_to || '',
        'Paid By': expense.paid_by || 'Company',
        'Payment Mode': expense.payment_mode || '',
        'Payment Status': expense.payment_status === 'paid' ? 'Paid' : expense.payment_status === 'partially_paid' ? 'Partially Paid' : 'Pending',
        'Paid Amount (₹)': expense.paid_amount || 0,
        'Settlement Status': expense.settlement_status === 'settled' ? 'Settled' : expense.settlement_status === 'partial' ? 'Partial' : 'Unsettled',
        'Receipt No': expense.receipt_no || '',
        'Payment Reference': expense.payment_reference || ''
      }));

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      ws['!cols'] = [
        { wch: 6 },   // S.No
        { wch: 12 },  // Date
        { wch: 15 },  // Category
        { wch: 30 },  // Description
        { wch: 10 },  // Rate
        { wch: 8 },   // Quantity
        { wch: 12 },  // Amount
        { wch: 10 },  // Vertical
        { wch: 20 },  // Paid To
        { wch: 15 },  // Paid By
        { wch: 12 },  // Payment Mode
        { wch: 14 },  // Payment Status
        { wch: 12 },  // Paid Amount
        { wch: 14 },  // Settlement Status
        { wch: 12 },  // Receipt No
        { wch: 15 }   // Payment Reference
      ];

      // Add summary row
      const totalRow = {
        'S.No': '',
        'Date': '',
        'Category': '',
        'Description': 'TOTAL',
        'Rate (₹)': '',
        'Quantity': '',
        'Amount (₹)': totalAmount,
        'Vertical': '',
        'Paid To': '',
        'Paid By': '',
        'Payment Mode': '',
        'Payment Status': '',
        'Paid Amount (₹)': expenses.reduce((sum, e) => sum + (e.paid_amount || 0), 0),
        'Settlement Status': '',
        'Receipt No': '',
        'Payment Reference': ''
      };
      XLSX.utils.sheet_add_json(ws, [totalRow], { skipHeader: true, origin: -1 });

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Variable Expenses');

      // Generate filename with date range
      const fromStr = filterDateFrom || 'start';
      const toStr = filterDateTo || new Date().toISOString().split('T')[0];
      const filename = `Variable_Expenses_${fromStr}_to_${toStr}.xlsx`;

      // Download the file
      XLSX.writeFile(wb, filename);
      toast.success(`Exported ${expenses.length} expenses to Excel`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export expenses');
    }
  };

  return (
    <Layout>
      <div data-testid="variable-expenses-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Variable Expenses</h1>
            <p className="text-sm text-gray-500">Track daily operational expenses</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={loadExpenses}>
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
            {/* Settle button - behavior depends on whether items are selected */}
            {(selectedExpenses.length > 0 || getVendorsWithPendingPayments().length > 0) && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  if (selectedExpenses.length > 0) {
                    // Individual items selected - open settlement dialog
                    setShowSettlementDialog(true);
                  } else {
                    // No items selected - open vendor payment modal
                    openVendorPaymentModal();
                  }
                }}
                className={selectedExpenses.length > 0 ? "text-green-600 border-green-300" : "text-purple-600 border-purple-300"}
                data-testid="settle-payments-btn"
              >
                <IndianRupee size={14} className="mr-1" /> 
                {selectedExpenses.length > 0 ? `Settle ${selectedExpenses.length} Selected` : 'Settle'}
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowVendorManagement(true)}
              className="text-gray-600"
            >
              <Building2 size={14} className="mr-1" /> Manage Vendors
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={exportToExcel}
              className="text-blue-600 border-blue-300 hover:bg-blue-50"
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet size={14} className="mr-1" /> Export Excel
            </Button>
            <Button size="sm" onClick={() => { resetForm(); setShowAddDialog(true); }} className="bg-[#14532D] hover:bg-[#166534]">
              <Plus size={14} className="mr-1" /> Add Expense
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Receipt className="text-blue-600" size={18} />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Expenses</p>
                <p className="text-lg font-bold">₹{totalAmount.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="text-orange-600" size={18} />
              </div>
              <div>
                <p className="text-xs text-gray-500">Unsettled</p>
                <p className="text-lg font-bold text-orange-600">₹{unsettledAmount.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="text-green-600" size={18} />
              </div>
              <div>
                <p className="text-xs text-gray-500">Paid</p>
                <p className="text-lg font-bold text-green-600">₹{paidAmount.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <IndianRupee className="text-purple-600" size={18} />
              </div>
              <div>
                <p className="text-xs text-gray-500">Pending Reimbursement</p>
                <p className="text-lg font-bold text-purple-600">₹{pendingReimbursementAmount.toLocaleString()}</p>
              </div>
            </div>
          </Card>
          <Card className="p-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertCircle className="text-yellow-600" size={18} />
              </div>
              <div>
                <p className="text-xs text-gray-500">Partially Paid</p>
                <p className="text-lg font-bold text-yellow-600">{partiallyPaidExpenses.length}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Pending Reimbursements by Employee */}
        {employeesWithPendingReimbursements.length > 0 && (
          <Card className="mb-4">
            <CardContent className="py-3">
              <h3 className="text-sm font-semibold text-purple-800 mb-3 flex items-center gap-2">
                <Users size={16} /> Pending Employee Reimbursements
              </h3>
              <div className="flex flex-wrap gap-2">
                {employeesWithPendingReimbursements.map(emp => {
                  const remainingAmount = emp.total - emp.totalReimbursed;
                  return (
                    <div 
                      key={emp.name}
                      className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2"
                    >
                      <div>
                        <span className="text-sm font-medium text-purple-800">{emp.name}</span>
                        <span className="text-xs text-purple-600 ml-2">({emp.expenses.length} items)</span>
                        <span className="text-sm font-bold text-purple-700 ml-2">₹{remainingAmount.toLocaleString()}</span>
                        {emp.totalReimbursed > 0 && (
                          <span className="text-xs text-green-600 ml-1">(₹{emp.totalReimbursed.toLocaleString()} paid)</span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className="h-7 bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={() => openBulkSettlementModal(emp)}
                      >
                        <IndianRupee size={12} className="mr-1" /> Settle All
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="py-3">
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">From Date</label>
                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="w-36 h-8 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">To Date</label>
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="w-36 h-8 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Category</label>
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-36 h-8 text-sm">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {EXPENSE_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Status</label>
                <Select value={filterSettled} onValueChange={setFilterSettled}>
                  <SelectTrigger className="w-32 h-8 text-sm">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="true">Settled</SelectItem>
                    <SelectItem value="false">Unsettled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Paid By</label>
                <Select value={filterPaidBy} onValueChange={setFilterPaidBy}>
                  <SelectTrigger className="w-36 h-8 text-sm">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Company">Company</SelectItem>
                    {staffUsers.map(user => (
                      <SelectItem key={user.id} value={user.name}>{user.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Paid To</label>
                <Input
                  type="text"
                  placeholder="Vendor name..."
                  value={filterPaidTo === 'all' ? '' : filterPaidTo}
                  onChange={(e) => setFilterPaidTo(e.target.value || 'all')}
                  className="w-36 h-8 text-sm"
                />
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); setFilterCategory('all'); setFilterSettled('all'); setFilterPaidBy('all'); setFilterPaidTo('all'); }}
              >
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Expenses Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#14532D]"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="p-2 text-left w-8">
                        <Checkbox 
                          checked={selectedExpenses.length === unsettledExpenses.length && unsettledExpenses.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedExpenses(unsettledExpenses.map(e => e.id));
                            } else {
                              setSelectedExpenses([]);
                            }
                          }}
                        />
                      </th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">DATE</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">CATEGORY</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">DESCRIPTION</th>
                      <th className="p-2 text-right text-xs font-medium text-gray-500">RATE</th>
                      <th className="p-2 text-right text-xs font-medium text-gray-500">QTY</th>
                      <th className="p-2 text-right text-xs font-medium text-gray-500">AMOUNT</th>
                      <th className="p-2 text-center text-xs font-medium text-gray-500">VERTICAL</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">PAID TO</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">PAID BY</th>
                      <th className="p-2 text-center text-xs font-medium text-gray-500">STATUS</th>
                      <th className="p-2 text-center text-xs font-medium text-gray-500">SETTLEMENT</th>
                      <th className="p-2 text-center text-xs font-medium text-gray-500">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.length === 0 ? (
                      <tr>
                        <td colSpan={13} className="p-8 text-center text-gray-500">
                          No expenses found. Click "Add Expense" to get started.
                        </td>
                      </tr>
                    ) : (
                      expenses.map((expense) => {
                        const statusConfig = PAYMENT_STATUS_OPTIONS.find(s => s.value === expense.payment_status) || PAYMENT_STATUS_OPTIONS[0];
                        // Show checkbox for: unpaid expenses OR employee-paid expenses pending reimbursement
                        // For employee-paid expenses, we don't require payment_status === 'paid'
                        const isPendingReimbursement = expense.paid_by_type === 'employee' && 
                          expense.settlement_status !== 'settled';
                        const showCheckbox = (expense.payment_status !== 'paid' && !expense.is_settled) || isPendingReimbursement;
                        return (
                        <tr key={expense.id} className={`border-b hover:bg-gray-50 ${showCheckbox ? 'bg-orange-50' : ''}`}>
                          <td className="p-2">
                            {showCheckbox && (
                              <Checkbox 
                                checked={selectedExpenses.includes(expense.id)}
                                onCheckedChange={() => toggleExpenseSelection(expense.id)}
                              />
                            )}
                          </td>
                          <td className="p-2 text-xs">
                            {new Date(expense.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          </td>
                          <td className="p-2">
                            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{expense.category}</span>
                          </td>
                          <td className="p-2 text-xs max-w-[150px] truncate">{expense.description || '-'}</td>
                          <td className="p-2 text-right text-xs text-gray-600">{expense.rate ? `₹${expense.rate.toLocaleString()}` : '-'}</td>
                          <td className="p-2 text-right text-xs text-gray-600">{expense.quantity || '-'}</td>
                          <td className="p-2 text-right font-medium">₹{expense.amount?.toLocaleString()}</td>
                          <td className="p-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              expense.vertical === 'qc' ? 'bg-blue-100 text-blue-700' :
                              expense.vertical === 'retail' ? 'bg-green-100 text-green-700' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {expense.vertical === 'qc' ? 'QC' : expense.vertical === 'retail' ? 'Retail' : 'All'}
                            </span>
                          </td>
                          <td className="p-2 text-xs">{expense.paid_to || '-'}</td>
                          <td className="p-2 text-xs">
                            <span className={`px-2 py-0.5 rounded ${
                              expense.paid_by === 'Company' || !expense.paid_by 
                                ? 'bg-blue-50 text-blue-700' 
                                : 'bg-purple-50 text-purple-700'
                            }`}>
                              {expense.paid_by || 'Company'}
                            </span>
                          </td>
                          <td className="p-2 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${statusConfig.color}`}>
                              {expense.payment_status === 'paid' || expense.is_settled ? <CheckCircle size={10} /> : 
                               expense.payment_status === 'partially_paid' ? <AlertCircle size={10} /> : <Clock size={10} />}
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="p-2 text-center">
                            {expense.paid_by_type === 'employee' ? (
                              expense.settlement_status === 'settled' ? (
                                <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">Settled</span>
                              ) : expense.settlement_status === 'partial' ? (
                                <div className="flex flex-col items-center gap-0.5">
                                  <div className="flex items-center gap-1">
                                    <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">Partial</span>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-5 w-5 p-0"
                                      onClick={() => openReimbursementModal(expense)}
                                      title="Record More Reimbursement"
                                    >
                                      <IndianRupee size={12} className="text-green-600" />
                                    </Button>
                                  </div>
                                  <span className="text-[10px] text-gray-500">
                                    ₹{(expense.total_reimbursed || 0).toLocaleString()} / ₹{expense.amount?.toLocaleString()}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center gap-1">
                                  <span className="px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700">Pending</span>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="h-5 w-5 p-0"
                                    onClick={() => openReimbursementModal(expense)}
                                    title="Record Reimbursement"
                                  >
                                    <IndianRupee size={12} className="text-green-600" />
                                  </Button>
                                </div>
                              )
                            ) : expense.payment_status === 'paid' ? (
                              <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">Settled</span>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>
                          <td className="p-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {/* View button for ALL expenses */}
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleViewPayment(expense)} title="View Details">
                                <Eye size={12} className="text-blue-600" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleCopyExpense(expense)} title="Copy Expense">
                                <Copy size={12} className="text-purple-600" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEdit(expense)}>
                                <Edit2 size={12} className="text-blue-600" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDelete(expense.id)}>
                                <Trash2 size={12} className="text-red-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )})
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Category Summary */}
        {Object.keys(categoryTotals).length > 0 && (
          <Card className="mt-4">
            <CardHeader className="py-3">
              <CardTitle className="text-sm">Category Breakdown</CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]).map(([cat, total]) => (
                  <div key={cat} className="p-2 bg-gray-50 rounded text-center">
                    <p className="text-xs text-gray-500">{cat}</p>
                    <p className="font-semibold text-sm">₹{total.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Variable Expense'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Date *</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Category *</label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPENSE_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Description</label>
                <Input
                  placeholder="Brief description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
              
              {/* Rate × Quantity = Amount */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Rate (₹)</label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={formData.rate}
                    onChange={(e) => {
                      const rate = e.target.value;
                      const qty = formData.quantity;
                      const calculatedAmount = rate && qty ? (parseFloat(rate) * parseFloat(qty)).toFixed(2) : formData.amount;
                      setFormData(prev => ({ ...prev, rate, amount: calculatedAmount }));
                    }}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Quantity</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={formData.quantity}
                    onChange={(e) => {
                      const qty = e.target.value;
                      const rate = formData.rate;
                      const calculatedAmount = rate && qty ? (parseFloat(rate) * parseFloat(qty)).toFixed(2) : formData.amount;
                      setFormData(prev => ({ ...prev, quantity: qty, amount: calculatedAmount }));
                    }}
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Amount (₹) *</label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    className="h-8 text-sm font-semibold bg-gray-50"
                  />
                  {formData.rate && formData.quantity && (
                    <p className="text-[10px] text-gray-400 mt-0.5">= {formData.rate} × {formData.quantity}</p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Payment Mode</label>
                  <Select value={formData.payment_mode} onValueChange={(v) => setFormData(prev => ({ ...prev, payment_mode: v }))}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_MODES.map(mode => (
                        <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Payment Status *</label>
                  <Select value={formData.payment_status} onValueChange={(v) => setFormData(prev => ({ ...prev, payment_status: v }))}>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_STATUS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Partially Paid - show paid amount */}
              {formData.payment_status === 'partially_paid' && (
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Amount Paid (₹)</label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={formData.paid_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, paid_amount: e.target.value }))}
                    className="h-8 text-sm"
                  />
                  {formData.amount && formData.paid_amount && (
                    <p className="text-[10px] text-orange-600 mt-0.5">
                      Balance: ₹{(parseFloat(formData.amount) - parseFloat(formData.paid_amount)).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-medium text-gray-700">Paid To (Vendor) *</label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-5 text-xs text-blue-600 hover:text-blue-800 p-0"
                      onClick={() => { setShowVendorDialog(true); resetVendorForm(); }}
                    >
                      <UserPlus size={12} className="mr-0.5" /> Add New
                    </Button>
                  </div>
                  <Select 
                    value={formData.vendor_id || ''} 
                    onValueChange={(v) => {
                      const vendor = vendors.find(vnd => vnd.id === v);
                      setFormData(prev => ({ 
                        ...prev, 
                        vendor_id: v,
                        paid_to: vendor?.name || ''
                      }));
                    }}
                  >
                    <SelectTrigger className={`h-8 text-sm ${!formData.vendor_id ? 'border-orange-300' : ''}`} data-testid="paid-to-select">
                      <SelectValue placeholder="Select Vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.filter(v => v.is_active !== false).map(vendor => (
                        <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {vendors.length === 0 && (
                    <p className="text-[10px] text-orange-500 mt-0.5">No vendors yet. Click "Add New" or use "Manage Vendors" to add.</p>
                  )}
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">
                    Transaction Ref {formData.payment_mode !== 'Cash' && <span className="text-red-500">*</span>}
                  </label>
                  <Input
                    placeholder={formData.payment_mode === 'Cash' ? "Optional for cash" : "Transaction ID required"}
                    value={formData.payment_reference}
                    onChange={(e) => setFormData(prev => ({ ...prev, payment_reference: e.target.value }))}
                    className={`h-8 text-sm ${formData.payment_mode !== 'Cash' && !formData.payment_reference?.trim() ? 'border-orange-300' : ''}`}
                    data-testid="payment-reference-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Receipt/Bill No</label>
                  <Input
                    placeholder="Optional"
                    value={formData.receipt_no}
                    onChange={(e) => setFormData(prev => ({ ...prev, receipt_no: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
              </div>

              {/* Remarks - optional free-text note about the expense */}
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Remarks</label>
                <Textarea
                  placeholder="Optional note about this expense..."
                  value={formData.remarks}
                  onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                  rows={2}
                  className="text-sm resize-none"
                  data-testid="expense-remarks-input"
                />
              </div>
              
              {/* Vertical Allocation */}
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Vertical Incurred For? *</label>
                <Select value={formData.vertical} onValueChange={(v) => setFormData(prev => ({ ...prev, vertical: v, retailer_id: '' }))}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select Vertical" />
                  </SelectTrigger>
                  <SelectContent>
                    {VERTICAL_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400 mt-1">
                  {formData.vertical === 'all' && 'Expense will be split equally between QC and Retail'}
                  {formData.vertical === 'qc' && 'Expense will be allocated only to QC vertical'}
                  {formData.vertical === 'retail' && 'Expense will be allocated only to Retail vertical'}
                </p>
              </div>
              
              {/* Split Type Selection - Only shown when vertical is 'retail' */}
              {formData.vertical === 'retail' && (
                <div className="space-y-3">
                  <label className="text-xs font-medium text-gray-700 mb-1 block">Split Type</label>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50">
                      <input 
                        type="radio"
                        name="split_type"
                        value="all_equal"
                        checked={formData.split_type === 'all_equal'}
                        onChange={() => setFormData(prev => ({ ...prev, split_type: 'all_equal', retailer_ids: [] }))}
                        className="mt-1"
                      />
                      <div>
                        <span className="text-sm font-medium">Split equally across all active retailers</span>
                        <p className="text-xs text-gray-500">For tech, marketing, admin expenses - split among all retailers active on expense date</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50">
                      <input 
                        type="radio"
                        name="split_type"
                        value="selected"
                        checked={formData.split_type === 'selected'}
                        onChange={() => setFormData(prev => ({ ...prev, split_type: 'selected' }))}
                        className="mt-1"
                      />
                      <div>
                        <span className="text-sm font-medium">Select specific retailers</span>
                        <p className="text-xs text-gray-500">Equal split among selected retailers only</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2 cursor-pointer p-2 rounded border hover:bg-gray-50">
                      <input 
                        type="radio"
                        name="split_type"
                        value="proportional"
                        checked={formData.split_type === 'proportional'}
                        onChange={() => setFormData(prev => ({ ...prev, split_type: 'proportional', retailer_ids: [] }))}
                        className="mt-1"
                      />
                      <div>
                        <span className="text-sm font-medium">Split proportionally by sales</span>
                        <p className="text-xs text-gray-500">Only retailers with sales on expense date, weighted by their sales amount</p>
                      </div>
                    </label>
                  </div>
                  
                  {/* Retailer multi-select - Only for 'selected' split type */}
                  {formData.split_type === 'selected' && (
                    <div className="mt-3 p-3 bg-gray-50 rounded border">
                      <label className="text-xs font-medium text-gray-700 mb-2 block">Select Retailers ({(formData.retailer_ids || []).length} selected)</label>
                      
                      {/* Selected Retailers as Chips */}
                      {(formData.retailer_ids || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3 p-2 bg-white rounded border">
                          {(formData.retailer_ids || []).map(rid => {
                            const retailer = retailers.find(r => r.id === rid);
                            return (
                              <span 
                                key={rid} 
                                className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full text-xs"
                              >
                                {retailer?.company_name || retailer?.name || rid}
                                <button 
                                  type="button"
                                  onClick={() => setFormData(prev => ({ ...prev, retailer_ids: (prev.retailer_ids || []).filter(id => id !== rid) }))}
                                  className="hover:bg-blue-200 rounded-full p-0.5"
                                >
                                  <X size={12} />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Search box */}
                      <div className="relative mb-2">
                        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                          type="text"
                          value={retailerPickerSearch}
                          onChange={(e) => setRetailerPickerSearch(e.target.value)}
                          placeholder="Search retailers..."
                          className="w-full pl-7 pr-7 py-1.5 text-sm border rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                          data-testid="expense-retailer-picker-search"
                        />
                        {retailerPickerSearch && (
                          <button
                            type="button"
                            onClick={() => setRetailerPickerSearch('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            data-testid="expense-retailer-picker-clear"
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>

                      {/* Retailer Checkbox List */}
                      <div className="max-h-40 overflow-y-auto space-y-1 border rounded bg-white p-2" data-testid="expense-retailer-picker-list">
                        {(() => {
                          const nameOf = (r) => (r.company_name || r.name || r.email || '');
                          const q = retailerPickerSearch.trim().toLowerCase();
                          const list = retailers
                            .filter(r => (r.status || 'active') !== 'churned')
                            .filter(r => !q || nameOf(r).toLowerCase().includes(q))
                            .sort((a, b) => nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: 'base' }));
                          if (list.length === 0) {
                            return <p className="text-xs text-gray-500 italic p-1">No retailers match "{retailerPickerSearch}"</p>;
                          }
                          return list.map(retailer => (
                            <label key={retailer.id} className="flex items-center gap-2 cursor-pointer text-sm hover:bg-gray-50 p-1 rounded">
                              <input
                                type="checkbox"
                                checked={(formData.retailer_ids || []).includes(retailer.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setFormData(prev => ({ ...prev, retailer_ids: [...(prev.retailer_ids || []), retailer.id] }));
                                  } else {
                                    setFormData(prev => ({ ...prev, retailer_ids: (prev.retailer_ids || []).filter(id => id !== retailer.id) }));
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              {nameOf(retailer)}
                            </label>
                          ));
                        })()}
                      </div>
                      {(formData.retailer_ids || []).length === 0 && (
                        <p className="text-xs text-red-500 mt-2">Please select at least one retailer</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Paid By Section */}
              <div className="border-t pt-3 mt-3">
                <label className="text-xs font-medium text-gray-700 mb-2 block">Paid By *</label>
                <div className="flex gap-4 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio"
                      name="paid_by_type"
                      value="company"
                      checked={formData.paid_by_type === 'company'}
                      onChange={() => setFormData(prev => ({ 
                        ...prev, 
                        paid_by_type: 'company',
                        paid_by: 'Company',
                        paid_by_employee_id: ''
                      }))}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="text-sm">Company</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio"
                      name="paid_by_type"
                      value="employee"
                      checked={formData.paid_by_type === 'employee'}
                      onChange={() => setFormData(prev => ({ 
                        ...prev, 
                        paid_by_type: 'employee',
                        paid_by: '',
                        paid_by_employee_id: ''
                      }))}
                      className="w-4 h-4 text-green-600"
                    />
                    <span className="text-sm">Employee (Reimbursement)</span>
                  </label>
                </div>
                
                {formData.paid_by_type === 'employee' && (
                  <div className="space-y-3 pl-4 border-l-2 border-blue-200">
                    <div>
                      <label className="text-xs font-medium text-gray-700 mb-1 block">Employee Name *</label>
                      <Select 
                        value={formData.paid_by_employee_id} 
                        onValueChange={(value) => {
                          const emp = staffUsers.find(u => u.id === value);
                          setFormData(prev => ({ 
                            ...prev, 
                            paid_by_employee_id: value,
                            paid_by: emp?.name || emp?.email || value
                          }));
                        }}
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue placeholder="Select employee who paid" />
                        </SelectTrigger>
                        <SelectContent>
                          {staffUsers.map(user => (
                            <SelectItem key={user.id} value={user.id}>{user.name} ({user.role})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => { setShowAddDialog(false); resetForm(); }}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSubmit} className="bg-[#14532D] hover:bg-[#166534]">
                {editingExpense ? 'Update' : 'Add Expense'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Settlement Dialog */}
        <Dialog open={showSettlementDialog} onOpenChange={setShowSettlementDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Settle Employee Expenses</DialogTitle>
            </DialogHeader>
            
            <div className="py-4">
              <p className="text-sm text-gray-600 mb-3">
                Select expenses to settle from the table, then click "Settle Selected" below.
              </p>
              
              <div className="bg-gray-50 p-3 rounded mb-3">
                <p className="text-xs text-gray-500">Selected Expenses</p>
                <p className="text-xl font-bold">{selectedExpenses.length} items</p>
                <p className="text-sm text-gray-600">
                  Total: ₹{expenses.filter(e => selectedExpenses.includes(e.id)).reduce((sum, e) => sum + (e.amount || 0), 0).toLocaleString()}
                </p>
              </div>
              
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Settlement Remarks</label>
                <Input
                  placeholder="e.g., Paid via bank transfer on..."
                  value={settlementRemarks}
                  onChange={(e) => setSettlementRemarks(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setShowSettlementDialog(false)}>
                Cancel
              </Button>
              <Button 
                size="sm" 
                onClick={handleBulkSettle} 
                disabled={selectedExpenses.length === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle size={14} className="mr-1" /> Settle Selected
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payment Details Modal - shown when marking expense as Paid */}
        {showPaymentDetailsModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="variable-payment-details-modal">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-4 border-b bg-green-50">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <CheckCircle size={20} className="text-green-600" />
                  Record Payment Details
                </h3>
                <p className="text-sm text-gray-500 mt-1">Expense amount: ₹{formData.amount}</p>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Payment Date *</label>
                  <Input
                    type="date"
                    value={paymentDetailsForm.payment_date}
                    onChange={(e) => setPaymentDetailsForm(prev => ({ ...prev, payment_date: e.target.value }))}
                    data-testid="variable-payment-date-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Payment Mode *</label>
                  <Select value={paymentDetailsForm.payment_mode} onValueChange={(v) => setPaymentDetailsForm(prev => ({ ...prev, payment_mode: v }))}>
                    <SelectTrigger data-testid="variable-payment-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                      <SelectItem value="Card">Card</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Reference / Transaction ID</label>
                  <Input
                    placeholder="Enter reference number"
                    value={paymentDetailsForm.payment_reference}
                    onChange={(e) => setPaymentDetailsForm(prev => ({ ...prev, payment_reference: e.target.value }))}
                    data-testid="variable-payment-reference-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Paid By</label>
                  <div className="flex gap-4 mb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio"
                        checked={paymentDetailsForm.paid_by_type === 'company'}
                        onChange={() => setPaymentDetailsForm(prev => ({ ...prev, paid_by_type: 'company', paid_by_employee_id: '' }))}
                        className="w-4 h-4 text-green-600"
                        data-testid="variable-paid-by-company-radio"
                      />
                      <span className="text-sm">Company</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio"
                        checked={paymentDetailsForm.paid_by_type === 'employee'}
                        onChange={() => setPaymentDetailsForm(prev => ({ ...prev, paid_by_type: 'employee' }))}
                        className="w-4 h-4 text-green-600"
                        data-testid="variable-paid-by-employee-radio"
                      />
                      <span className="text-sm">Employee</span>
                    </label>
                  </div>
                  {paymentDetailsForm.paid_by_type === 'employee' && (
                    <Select 
                      value={paymentDetailsForm.paid_by_employee_id} 
                      onValueChange={(v) => setPaymentDetailsForm(prev => ({ ...prev, paid_by_employee_id: v }))}
                    >
                      <SelectTrigger data-testid="variable-paid-by-employee-select">
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {staffUsers.map(user => (
                          <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
              <div className="p-4 border-t flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1" 
                  onClick={() => {
                    setShowPaymentDetailsModal(false);
                    setShowAddDialog(true); // Re-open Add Dialog to allow user to edit
                  }}
                  data-testid="variable-payment-cancel-btn"
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-green-600 hover:bg-green-700" 
                  onClick={handlePaymentDetailsSubmit}
                  data-testid="variable-confirm-payment-btn"
                >
                  <CheckCircle size={14} className="mr-1" /> Confirm Payment
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* View Expense Modal - Shows ALL details */}
        {showViewPaymentModal && viewingExpense && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col">
              <div className="p-4 border-b bg-blue-50 flex-shrink-0">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Eye size={20} className="text-blue-600" />
                  Expense Details
                </h3>
              </div>
              <div className="p-4 space-y-4 overflow-y-auto flex-1">
                {/* Basic Expense Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Date</p>
                    <p className="font-medium">{viewingExpense.date?.split('T')[0]}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Category</p>
                    <p className="font-medium">{viewingExpense.category || '-'}</p>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Description</p>
                  <p className="font-medium">{viewingExpense.description || '-'}</p>
                </div>
                
                {/* Amount Details */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">Rate</p>
                    <p className="font-medium">₹{viewingExpense.rate || viewingExpense.amount || 0}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">Qty</p>
                    <p className="font-medium">{viewingExpense.quantity || 1}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                    <p className="text-xs text-gray-500 mb-1">Amount</p>
                    <p className="font-bold text-lg text-green-700">₹{(viewingExpense.amount || 0).toLocaleString()}</p>
                  </div>
                </div>
                
                {/* Vendor & Payment Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Paid To (Vendor)</p>
                    <p className="font-medium">{viewingExpense.paid_to || '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Payment Mode</p>
                    <p className="font-medium">{viewingExpense.payment_mode || 'Cash'}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Payment Status</p>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      viewingExpense.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                      viewingExpense.payment_status === 'partially_paid' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {viewingExpense.payment_status === 'paid' ? 'Paid' : 
                       viewingExpense.payment_status === 'partially_paid' ? 'Partially Paid' : 'Pending'}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Receipt/Bill No</p>
                    <p className="font-medium">{viewingExpense.receipt_no || '-'}</p>
                  </div>
                </div>

                {viewingExpense.remarks && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Remarks</p>
                    <p className="text-sm whitespace-pre-wrap">{viewingExpense.remarks}</p>
                  </div>
                )}
                
                {/* Vertical & Split Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Vertical</p>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      viewingExpense.vertical === 'qc' ? 'bg-purple-100 text-purple-700' :
                      viewingExpense.vertical === 'retail' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-200 text-gray-700'
                    }`}>
                      {viewingExpense.vertical === 'qc' ? 'QC Only' : 
                       viewingExpense.vertical === 'retail' ? 'Retail Only' : 'All (Split Equally)'}
                    </span>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Split Type</p>
                    <p className="font-medium text-sm">
                      {viewingExpense.split_type === 'selected' ? 'Selected Retailers' :
                       viewingExpense.split_type === 'proportional' ? 'Proportional by Sales' :
                       'All Active Retailers'}
                    </p>
                  </div>
                </div>
                
                {/* Selected Retailers - if split_type is 'selected' */}
                {viewingExpense.split_type === 'selected' && viewingExpense.retailer_ids?.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p className="text-xs text-gray-500 mb-2">Selected Retailers ({viewingExpense.retailer_ids.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {viewingExpense.retailer_ids.map(rid => {
                        const retailer = retailers.find(r => r.id === rid);
                        return (
                          <span key={rid} className="bg-white px-2 py-0.5 rounded text-xs border">
                            {retailer?.company_name || retailer?.name || rid}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Paid By Info */}
                <div className={`rounded-lg p-3 ${viewingExpense.paid_by_type === 'employee' ? 'bg-purple-50 border border-purple-200' : 'bg-blue-50 border border-blue-200'}`}>
                  <p className="text-xs text-gray-500 mb-1">Paid By</p>
                  <div className="flex items-center gap-2">
                    {viewingExpense.paid_by_type === 'employee' ? (
                      <span className="text-purple-600 bg-purple-100 px-2 py-0.5 rounded text-xs font-medium">EMPLOYEE</span>
                    ) : (
                      <span className="text-blue-600 bg-blue-100 px-2 py-0.5 rounded text-xs font-medium">COMPANY</span>
                    )}
                    <span className="font-medium">{viewingExpense.paid_by || 'Company'}</span>
                  </div>
                  {viewingExpense.payment_reference && (
                    <p className="text-sm mt-1 text-gray-600">Ref: {viewingExpense.payment_reference}</p>
                  )}
                  {viewingExpense.payment_date && (
                    <p className="text-sm text-gray-600">Payment Date: {viewingExpense.payment_date?.split('T')[0]}</p>
                  )}
                </div>
                
                {/* Settlement/Reimbursement Section - Only for employee paid */}
                {viewingExpense.paid_by_type === 'employee' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                      <Clock size={14} /> Reimbursement Status
                    </h4>
                    {viewingExpense.settlement_status === 'settled' && (
                      <div className="bg-white border border-green-100 rounded p-2 text-xs">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-semibold text-green-700">₹{(viewingExpense.total_reimbursed || viewingExpense.amount || 0).toFixed(2)}</span>
                          <span className="text-gray-500 uppercase text-[10px] bg-gray-100 px-1 rounded">{viewingExpense.settlement_mode || 'Bank Transfer'}</span>
                          <span className="text-green-600 bg-green-100 px-1.5 py-0.5 rounded text-[10px] font-medium">REIMBURSED</span>
                        </div>
                        {viewingExpense.settlement_reference && (
                          <p className="text-blue-700 font-medium mb-1">Ref #: {viewingExpense.settlement_reference}</p>
                        )}
                        {viewingExpense.settlement_remarks && (
                          <p className="text-gray-600 italic mb-1">"{viewingExpense.settlement_remarks}"</p>
                        )}
                        <p className="text-gray-600">Settled on: {viewingExpense.settlement_date?.split('T')[0]}</p>
                      </div>
                    )}
                    {viewingExpense.settlement_status === 'partial' && (
                      <div className="bg-white border border-orange-100 rounded p-2 text-xs">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-semibold text-orange-700">₹{(viewingExpense.total_reimbursed || 0).toFixed(2)}</span>
                          <span className="text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded text-[10px] font-medium">PARTIAL</span>
                        </div>
                        <p className="text-gray-600">Remaining: ₹{((viewingExpense.amount || 0) - (viewingExpense.total_reimbursed || 0)).toFixed(2)}</p>
                      </div>
                    )}
                    {(viewingExpense.settlement_status === 'pending_reimbursement' || !viewingExpense.settlement_status || viewingExpense.settlement_status === 'pending') && (
                      <div className="bg-orange-50 border border-orange-200 rounded p-2 text-xs">
                        <span className="text-orange-700 font-medium">⏳ Reimbursement pending to {viewingExpense.paid_by}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Settle Button for Employee Paid Expenses */}
                {viewingExpense.paid_by_type === 'employee' && viewingExpense.settlement_status !== 'settled' && (
                  <Button 
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={() => { setShowViewPaymentModal(false); openReimbursementModal(viewingExpense); }}
                  >
                    <IndianRupee size={14} className="mr-1" /> Record Reimbursement
                  </Button>
                )}
              </div>
              <div className="p-4 border-t flex gap-2 flex-shrink-0">
                <Button variant="outline" className="flex-1" onClick={() => setShowViewPaymentModal(false)}>
                  Close
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => { setShowViewPaymentModal(false); handleEdit(viewingExpense); }}>
                  <Edit2 size={14} className="mr-1" /> Edit
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Reimbursement Modal - Single Expense */}
        {showReimbursementModal && reimbursementExpense && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-4 border-b bg-purple-50">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <IndianRupee size={20} className="text-purple-600" />
                  Record Reimbursement
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Reimburse {reimbursementExpense.paid_by} for ₹{reimbursementExpense.amount?.toLocaleString()}
                </p>
              </div>
              <div className="p-4 space-y-4">
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600">Category:</span>
                    <span className="font-medium">{reimbursementExpense.category}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600">Description:</span>
                    <span className="font-medium">{reimbursementExpense.description || '-'}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="font-medium">₹{reimbursementExpense.amount?.toLocaleString()}</span>
                  </div>
                  {(reimbursementExpense.total_reimbursed || 0) > 0 && (
                    <>
                      <div className="flex justify-between mb-1">
                        <span className="text-gray-600">Already Reimbursed:</span>
                        <span className="font-medium text-green-600">₹{(reimbursementExpense.total_reimbursed || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between border-t pt-1 mt-1">
                        <span className="text-gray-600 font-medium">Remaining:</span>
                        <span className="font-bold text-purple-600">₹{(reimbursementExpense.amount - (reimbursementExpense.total_reimbursed || 0)).toLocaleString()}</span>
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Reimbursement Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <Input
                      type="number"
                      value={reimbursementForm.amount}
                      onChange={(e) => setReimbursementForm(prev => ({ ...prev, amount: e.target.value }))}
                      className="pl-7"
                      placeholder="Enter amount to reimburse"
                      max={reimbursementExpense.amount - (reimbursementExpense.total_reimbursed || 0)}
                    />
                  </div>
                  {parseFloat(reimbursementForm.amount) < (reimbursementExpense.amount - (reimbursementExpense.total_reimbursed || 0)) && parseFloat(reimbursementForm.amount) > 0 && (
                    <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                      <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                      Partial reimbursement - Status will be set to "Partial"
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Reimbursement Date *</label>
                  <Input
                    type="date"
                    value={reimbursementForm.payment_date}
                    onChange={(e) => setReimbursementForm(prev => ({ ...prev, payment_date: e.target.value }))}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Payment Mode *</label>
                  <Select value={reimbursementForm.payment_mode} onValueChange={(v) => setReimbursementForm(prev => ({ ...prev, payment_mode: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Reference / Transaction ID</label>
                  <Input
                    value={reimbursementForm.payment_reference}
                    onChange={(e) => setReimbursementForm(prev => ({ ...prev, payment_reference: e.target.value }))}
                    placeholder="Enter reference number"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Remarks</label>
                  <Input
                    value={reimbursementForm.remarks}
                    onChange={(e) => setReimbursementForm(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
              <div className="p-4 border-t flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setShowReimbursementModal(false); setReimbursementExpense(null); }}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-purple-600 hover:bg-purple-700" 
                  onClick={handleReimbursementSubmit}
                  disabled={!reimbursementForm.amount || parseFloat(reimbursementForm.amount) <= 0}
                >
                  <CheckCircle size={14} className="mr-1" /> Confirm Reimbursement
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Settlement Modal */}
        {showBulkSettlementModal && bulkSettlementEmployee && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
              <div className="p-4 border-b bg-purple-50 flex-shrink-0">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Users size={20} className="text-purple-600" />
                      Reimburse {bulkSettlementEmployee.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {bulkSettlementEmployee.expenses.length} pending expense(s)
                    </p>
                  </div>
                  <button onClick={() => { setShowBulkSettlementModal(false); setBulkSettlementEmployee(null); }} className="text-gray-500 hover:text-gray-700">✕</button>
                </div>
              </div>
              
              <div className="p-4 space-y-4 overflow-y-auto flex-1">
                {/* Reimbursement Amount Section */}
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-purple-700">Total Pending Reimbursement:</span>
                    <span className="text-lg font-bold text-purple-700">
                      ₹{bulkSettlementEmployee.total_remaining?.toLocaleString() || bulkSettlementEmployee.total?.toLocaleString()}
                    </span>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-purple-800 mb-1 block">Reimbursement Amount</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder={(bulkSettlementEmployee.total_remaining || bulkSettlementEmployee.total || 0).toFixed(2)}
                          className="pl-7 text-lg font-semibold"
                          data-testid="employee-reimbursement-amount"
                          value={reimbursementForm.custom_amount}
                          onChange={(e) => setReimbursementForm(prev => ({ ...prev, custom_amount: e.target.value }))}
                        />
                      </div>
                      <Button 
                        type="button"
                        variant="outline"
                        size="sm"
                        className="whitespace-nowrap"
                        onClick={() => setReimbursementForm(prev => ({ 
                          ...prev, 
                          custom_amount: (bulkSettlementEmployee.total_remaining || bulkSettlementEmployee.total || 0).toFixed(2)
                        }))}
                      >
                        Full Amount
                      </Button>
                    </div>
                    <p className="text-xs text-purple-600 mt-1">
                      Enter custom amount or leave empty to reimburse full pending. Amount applies to oldest expenses first.
                    </p>
                  </div>
                  
                  {/* Allocation Preview */}
                  {reimbursementForm.custom_amount && parseFloat(reimbursementForm.custom_amount) > 0 && (() => {
                    const reimbAmount = parseFloat(reimbursementForm.custom_amount);
                    const sortedExpenses = [...bulkSettlementEmployee.expenses].sort(
                      (a, b) => new Date(a.date) - new Date(b.date)
                    );
                    
                    let remaining = reimbAmount;
                    const allocations = [];
                    for (const expense of sortedExpenses) {
                      if (remaining <= 0) break;
                      const expRemaining = expense.remaining_amount || ((expense.amount || 0) - (expense.total_reimbursed || 0));
                      if (expRemaining <= 0) continue;
                      const allocAmt = Math.min(remaining, expRemaining);
                      allocations.push({
                        date: expense.date,
                        category: expense.category,
                        amount: allocAmt,
                        isPartial: allocAmt < expRemaining
                      });
                      remaining -= allocAmt;
                    }
                    
                    const totalPending = bulkSettlementEmployee.total_remaining || bulkSettlementEmployee.total || 0;
                    
                    return (
                      <div className="mt-2 pt-2 border-t border-purple-200">
                        <p className="text-xs font-medium text-purple-800 mb-1">Allocation Preview (oldest first):</p>
                        <div className="max-h-24 overflow-y-auto space-y-1">
                          {allocations.map((alloc, idx) => (
                            <div key={idx} className="flex justify-between text-xs">
                              <span className="text-gray-600">
                                {new Date(alloc.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} - {alloc.category}
                                {alloc.isPartial && <span className="text-orange-600 ml-1">(Partial)</span>}
                              </span>
                              <span className={alloc.isPartial ? 'text-orange-600' : 'text-purple-600'}>
                                ₹{alloc.amount.toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                        {remaining > 0 && (
                          <p className="text-xs text-orange-600 mt-1">
                            ⚠️ ₹{remaining.toFixed(2)} exceeds pending amount
                          </p>
                        )}
                        {reimbAmount < totalPending && (
                          <p className="text-xs text-blue-600 mt-1">
                            Remaining after this reimbursement: ₹{(totalPending - reimbAmount).toFixed(2)}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Payment Details */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Reimbursement Date *</label>
                    <Input
                      type="date"
                      value={reimbursementForm.payment_date}
                      onChange={(e) => setReimbursementForm(prev => ({ ...prev, payment_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Payment Mode *</label>
                    <Select value={reimbursementForm.payment_mode} onValueChange={(v) => setReimbursementForm(prev => ({ ...prev, payment_mode: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                        <SelectItem value="UPI">UPI</SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">
                    Reference / Transaction ID {reimbursementForm.payment_mode !== 'Cash' && <span className="text-red-500">*</span>}
                  </label>
                  <Input
                    value={reimbursementForm.payment_reference}
                    onChange={(e) => setReimbursementForm(prev => ({ ...prev, payment_reference: e.target.value }))}
                    placeholder={reimbursementForm.payment_mode === 'Cash' ? "Optional for cash" : "Required for non-cash payments"}
                    className={reimbursementForm.payment_mode !== 'Cash' && !reimbursementForm.payment_reference?.trim() ? 'border-orange-300' : ''}
                    data-testid="employee-reimbursement-reference"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Remarks</label>
                  <Input
                    value={reimbursementForm.remarks}
                    onChange={(e) => setReimbursementForm(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Optional notes for this reimbursement..."
                    data-testid="bulk-settlement-remarks"
                  />
                </div>
              </div>
              
              <div className="p-4 border-t flex gap-2 flex-shrink-0">
                <Button variant="outline" className="flex-1" onClick={() => { setShowBulkSettlementModal(false); setBulkSettlementEmployee(null); }}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-purple-600 hover:bg-purple-700" 
                  onClick={handleBulkSettlement}
                  data-testid="confirm-employee-reimbursement-btn"
                >
                  <IndianRupee size={14} className="mr-1" /> Confirm Reimbursement
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Vendor Bulk Payment Modal */}
        {showVendorPaymentModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold text-lg">Settle Vendor Payments</h3>
                <button onClick={() => setShowVendorPaymentModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
              </div>
              
              <div className="p-4 space-y-4 overflow-y-auto flex-1">
                {/* Vendor Selection */}
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Select Vendor/Employee</label>
                  <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                    {vendorsWithPending.map(vendor => (
                      <div
                        key={vendor.name}
                        onClick={() => setSelectedVendorForPayment(vendor)}
                        data-testid={`vendor-payment-row-${vendor.name.replace(/\s+/g, '-').toLowerCase()}`}
                        className={`p-3 rounded-lg cursor-pointer border transition-all ${
                          selectedVendorForPayment?.name === vendor.name 
                            ? 'border-purple-500 bg-purple-50' 
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900">{vendor.name}</p>
                            <p className="text-xs text-gray-500">{vendor.expenses.length} pending expense(s)</p>
                          </div>
                          <p className="font-bold text-purple-700">₹{vendor.total_pending.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                    {vendorsWithPending.length === 0 && (
                      <p className="text-center text-gray-500 py-4">No pending payments</p>
                    )}
                  </div>
                </div>

                {selectedVendorForPayment && (
                  <>
                    {/* Payment Amount */}
                    <div className="bg-green-50 p-4 rounded-lg border border-green-200 space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-green-700">Total Pending:</span>
                        <span className="text-lg font-bold text-green-700">
                          ₹{selectedVendorForPayment.total_pending.toLocaleString()}
                        </span>
                      </div>
                      
                      <div>
                        <label className="text-xs font-medium text-green-800 mb-1 block">Payment Amount</label>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder={selectedVendorForPayment.total_pending.toFixed(2)}
                              className="pl-7 text-lg font-semibold"
                              data-testid="vendor-payment-amount"
                              value={vendorPaymentForm.custom_amount}
                              onChange={(e) => setVendorPaymentForm(prev => ({ ...prev, custom_amount: e.target.value }))}
                            />
                          </div>
                          <Button 
                            type="button"
                            variant="outline"
                            size="sm"
                            className="whitespace-nowrap"
                            onClick={() => setVendorPaymentForm(prev => ({ 
                              ...prev, 
                              custom_amount: selectedVendorForPayment.total_pending.toFixed(2)
                            }))}
                          >
                            Full Amount
                          </Button>
                        </div>
                        <p className="text-xs text-green-600 mt-1">
                          Enter custom amount or leave empty to pay full pending. Payment applies to oldest entries first.
                        </p>
                      </div>
                      
                      {/* Allocation Preview */}
                      {vendorPaymentForm.custom_amount && parseFloat(vendorPaymentForm.custom_amount) > 0 && (() => {
                        const paymentAmount = parseFloat(vendorPaymentForm.custom_amount);
                        const sortedExpenses = [...selectedVendorForPayment.expenses].sort(
                          (a, b) => new Date(a.date) - new Date(b.date)
                        );
                        
                        let remaining = paymentAmount;
                        const allocations = [];
                        for (const expense of sortedExpenses) {
                          if (remaining <= 0) break;
                          const pending = (expense.amount || 0) - (expense.paid_amount || 0);
                          const payAmt = Math.min(remaining, pending);
                          allocations.push({
                            date: expense.date,
                            category: expense.category,
                            amount: payAmt,
                            isPartial: payAmt < pending
                          });
                          remaining -= payAmt;
                        }
                        
                        return (
                          <div className="mt-2 pt-2 border-t border-green-200">
                            <p className="text-xs font-medium text-green-800 mb-1">Allocation Preview (oldest first):</p>
                            <div className="max-h-24 overflow-y-auto space-y-1">
                              {allocations.map((alloc, idx) => (
                                <div key={idx} className="flex justify-between text-xs">
                                  <span className="text-gray-600">
                                    {new Date(alloc.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} - {alloc.category}
                                    {alloc.isPartial && <span className="text-orange-600 ml-1">(Partial)</span>}
                                  </span>
                                  <span className={alloc.isPartial ? 'text-orange-600' : 'text-green-600'}>
                                    ₹{alloc.amount.toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                            {remaining > 0 && (
                              <p className="text-xs text-orange-600 mt-1">
                                ⚠️ ₹{remaining.toFixed(2)} exceeds pending amount
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Payment Details */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">Payment Mode *</label>
                        <Select value={vendorPaymentForm.payment_mode} onValueChange={(v) => setVendorPaymentForm(prev => ({ ...prev, payment_mode: v }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_MODES.map(mode => (
                              <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1 block">
                          Reference {vendorPaymentForm.payment_mode !== 'Cash' && <span className="text-red-500">*</span>}
                        </label>
                        <Input
                          placeholder={vendorPaymentForm.payment_mode === 'Cash' ? "Optional" : "Required for non-cash"}
                          value={vendorPaymentForm.reference}
                          onChange={(e) => setVendorPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                          className={vendorPaymentForm.payment_mode !== 'Cash' && !vendorPaymentForm.reference?.trim() ? 'border-orange-300' : ''}
                          data-testid="vendor-payment-reference"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Remarks</label>
                      <Input
                        placeholder="Optional notes..."
                        value={vendorPaymentForm.remarks}
                        onChange={(e) => setVendorPaymentForm(prev => ({ ...prev, remarks: e.target.value }))}
                      />
                    </div>
                  </>
                )}
              </div>
              
              <div className="p-4 border-t flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowVendorPaymentModal(false)}>
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-purple-600 hover:bg-purple-700" 
                  onClick={handleVendorBulkPayment}
                  disabled={!selectedVendorForPayment}
                  data-testid="confirm-vendor-payment-btn"
                >
                  <IndianRupee size={14} className="mr-1" /> Confirm Payment
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Vendor Management Dialog */}
        <Dialog open={showVendorManagement} onOpenChange={setShowVendorManagement}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 size={20} /> Vendor Management
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Actions */}
              <div className="flex justify-between items-center">
                <p className="text-sm text-gray-500">{vendors.length} vendors</p>
                <div className="flex gap-2">
                  {vendors.length === 0 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={migrateVendorsFromExpenses}
                      className="text-orange-600"
                    >
                      <RefreshCw size={14} className="mr-1" /> Import from Expenses
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    onClick={() => { resetVendorForm(); setShowVendorDialog(true); }}
                    className="bg-[#14532D] hover:bg-[#166534]"
                  >
                    <Plus size={14} className="mr-1" /> Add Vendor
                  </Button>
                </div>
              </div>
              
              {/* Vendor List */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">VENDOR NAME</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">CONTACT</th>
                      <th className="p-2 text-left text-xs font-medium text-gray-500">PHONE</th>
                      <th className="p-2 text-center text-xs font-medium text-gray-500">STATUS</th>
                      <th className="p-2 text-center text-xs font-medium text-gray-500">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="p-8 text-center text-gray-500">
                          No vendors added yet. Click "Add Vendor" to create your first vendor, or "Import from Expenses" to migrate existing vendor names.
                        </td>
                      </tr>
                    ) : (
                      vendors.map(vendor => (
                        <tr key={vendor.id} className={`border-t hover:bg-gray-50 ${vendor.is_active === false ? 'opacity-50' : ''}`}>
                          <td className="p-2 font-medium">{vendor.name}</td>
                          <td className="p-2 text-gray-600">{vendor.contact || '-'}</td>
                          <td className="p-2 text-gray-600">{vendor.phone || '-'}</td>
                          <td className="p-2 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs ${vendor.is_active === false ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                              {vendor.is_active === false ? 'Inactive' : 'Active'}
                            </span>
                          </td>
                          <td className="p-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleEditVendor(vendor)}>
                                <Edit2 size={12} className="text-blue-600" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDeleteVendor(vendor.id)}>
                                <Trash2 size={12} className="text-red-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowVendorManagement(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Vendor Dialog */}
        <Dialog open={showVendorDialog} onOpenChange={setShowVendorDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{editingVendor ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-3 py-2">
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Vendor Name *</label>
                <Input
                  placeholder="Enter vendor name"
                  value={vendorForm.name}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, name: e.target.value }))}
                  className="h-8 text-sm"
                  data-testid="vendor-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Contact Person</label>
                <Input
                  placeholder="Contact person name"
                  value={vendorForm.contact}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, contact: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Phone Number</label>
                <Input
                  placeholder="Phone number"
                  value={vendorForm.phone}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 mb-1 block">Notes</label>
                <Input
                  placeholder="Additional notes (optional)"
                  value={vendorForm.notes}
                  onChange={(e) => setVendorForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowVendorDialog(false); resetVendorForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleVendorSubmit} className="bg-[#14532D] hover:bg-[#166534]">
                {editingVendor ? 'Update' : 'Add Vendor'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
